import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Check, CreditCard, Smartphone, Signal, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import axios from "axios";
import { AgentPaymentButton, isAgentLoggedIn } from "@/components/AgentPaymentFlow";
import { PaymentDisabledBanner } from "@/components/PaymentGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Operator styling
const OPERATOR_STYLES = {
  "TELKOMSEL": { color: "from-red-500 to-red-600", bgColor: "bg-red-100", textColor: "text-red-600" },
  "INDOSAT": { color: "from-yellow-500 to-yellow-600", bgColor: "bg-yellow-100", textColor: "text-yellow-600" },
  "XL": { color: "from-blue-500 to-blue-600", bgColor: "bg-blue-100", textColor: "text-blue-600" },
  "TRI": { color: "from-purple-500 to-purple-600", bgColor: "bg-purple-100", textColor: "text-purple-600" },
  "AXIS": { color: "from-purple-600 to-pink-600", bgColor: "bg-purple-100", textColor: "text-purple-600" },
  "SMARTFREN": { color: "from-red-600 to-orange-600", bgColor: "bg-red-100", textColor: "text-red-600" },
  "by.U": { color: "from-teal-500 to-cyan-600", bgColor: "bg-teal-100", textColor: "text-teal-600" },
};

// Prefix mapping for operator detection
const OPERATOR_PREFIXES = {
  "0811": "TELKOMSEL", "0812": "TELKOMSEL", "0813": "TELKOMSEL", "0821": "TELKOMSEL", 
  "0822": "TELKOMSEL", "0823": "TELKOMSEL", "0851": "TELKOMSEL", "0852": "TELKOMSEL", "0853": "TELKOMSEL",
  "0814": "INDOSAT", "0815": "INDOSAT", "0816": "INDOSAT", "0855": "INDOSAT", 
  "0856": "INDOSAT", "0857": "INDOSAT", "0858": "INDOSAT",
  "0817": "XL", "0818": "XL", "0819": "XL", "0859": "XL", "0877": "XL", "0878": "XL",
  "0895": "TRI", "0896": "TRI", "0897": "TRI", "0898": "TRI", "0899": "TRI",
  "0831": "AXIS", "0832": "AXIS", "0833": "AXIS", "0838": "AXIS",
  "0881": "SMARTFREN", "0882": "SMARTFREN", "0883": "SMARTFREN", "0884": "SMARTFREN", 
  "0885": "SMARTFREN", "0886": "SMARTFREN", "0887": "SMARTFREN", "0888": "SMARTFREN", "0889": "SMARTFREN",
};

function detectOperator(phoneNumber) {
  let normalized = phoneNumber.replace(/\D/g, '');
  if (normalized.startsWith('62')) {
    normalized = '0' + normalized.slice(2);
  }
  if (!normalized.startsWith('0')) {
    normalized = '0' + normalized;
  }
  
  const prefix = normalized.slice(0, 4);
  return OPERATOR_PREFIXES[prefix] || null;
}

function formatPhoneNumber(number) {
  let normalized = number.replace(/\D/g, '');
  if (normalized.startsWith('62')) {
    normalized = '0' + normalized.slice(2);
  }
  if (!normalized.startsWith('0')) {
    normalized = '0' + normalized;
  }
  return normalized;
}

export default function Pulsa() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const phoneNumber = searchParams.get("id");
  
  const [operator, setOperator] = useState(null);
  const [operatorStyle, setOperatorStyle] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [adminFee, setAdminFee] = useState(1000);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    if (!phoneNumber) {
      navigate("/");
      return;
    }
    const detected = detectOperator(phoneNumber);
    if (detected) {
      setOperator(detected);
      setOperatorStyle(OPERATOR_STYLES[detected] || OPERATOR_STYLES["TELKOMSEL"]);
    } else {
      toast.error("Operator tidak dikenali. Pastikan nomor HP benar.");
    }
    fetchAdminFee();
    fetchProducts();
  }, [phoneNumber, navigate]);

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await axios.get(`${API}/digiflazz/products/pulsa`);
      const brands = response.data.brands || {};
      setProducts(brands);
    } catch (err) {
      console.error("Failed to load products:", err);
      toast.error("Gagal memuat daftar produk dari DigiFlazz");
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchAdminFee = async () => {
    try {
      const response = await axios.get(`${API}/admin/fees/pulsa`);
      setAdminFee(response.data.fee || 1000);
    } catch (err) {
      setAdminFee(1000);
    }
  };

  const handlePurchase = async () => {
    if (!selectedProduct) {
      toast.error("Pilih nominal pulsa terlebih dahulu");
      return;
    }
    
    setIsProcessing(true);
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      navigate(`/payment-pulsa?phone=${formattedPhone}&nominal=${selectedProduct.selling_price}&sku=${selectedProduct.sku}&operator=${operator}&fee=${adminFee}&name=${encodeURIComponent(selectedProduct.name)}`);
    } catch (err) {
      toast.error("Gagal memproses pembelian");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get products for detected operator
  const operatorProducts = operator ? (products[operator] || []) : [];
  const sortedProducts = [...operatorProducts].sort((a, b) => a.price - b.price);
  const totalBayar = selectedProduct ? selectedProduct.selling_price + adminFee : 0;

  if (!operator) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="text-center p-8">
          <Phone className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
            Operator Tidak Dikenali
          </h2>
          <p className="text-slate-600 mb-6">
            Nomor {phoneNumber} tidak terdeteksi operatornya.
          </p>
          <Link to="/">
            <Button className="rounded-full">Kembali</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-3xl mx-auto">
            <PaymentDisabledBanner />
            {/* Header with operator info */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${operatorStyle?.color || 'from-slate-500 to-slate-600'} flex items-center justify-center`}>
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">
                    Pulsa {operator}
                  </h1>
                  <p className="text-slate-600 text-sm">
                    No. HP: <span className={`font-semibold ${operatorStyle?.textColor || 'text-slate-600'}`}>
                      {formatPhoneNumber(phoneNumber)}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Phone Info Card */}
            <Card className="glass-card-solid mb-6">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl ${operatorStyle?.bgColor || 'bg-slate-100'} flex items-center justify-center`}>
                    <Signal className={`w-7 h-7 ${operatorStyle?.textColor || 'text-slate-600'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Nomor Tujuan</p>
                    <p className="font-heading font-bold text-xl text-slate-800">
                      {formatPhoneNumber(phoneNumber)}
                    </p>
                    <p className={`text-sm font-medium ${operatorStyle?.textColor || 'text-slate-600'}`}>
                      {operator}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Nominal Selection */}
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-slate-800 mb-4">
                Pilih Nominal Pulsa
                {productsLoading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
              </h3>
              
              {productsLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[1,2,3,4].map(i => (
                    <Skeleton key={i} className="h-20 rounded-xl" />
                  ))}
                </div>
              ) : sortedProducts.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Tidak ada produk pulsa {operator} tersedia saat ini. Coba lagi nanti.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {sortedProducts.map((product) => (
                    <motion.button
                      key={product.sku}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedProduct(product)}
                      className={`relative p-4 rounded-xl border-2 transition-all text-center ${
                        selectedProduct?.sku === product.sku
                          ? `border-emerald-500 bg-emerald-50 shadow-md`
                          : "border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm"
                      }`}
                      data-testid={`nominal-${product.sku}`}
                    >
                      {selectedProduct?.sku === product.sku && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <p className={`font-semibold text-sm ${
                        selectedProduct?.sku === product.sku ? "text-emerald-600" : "text-slate-800"
                      }`}>
                        {product.name}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatCurrency(product.selling_price)}
                      </p>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Purchase Summary */}
            {selectedProduct && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="glass-card border-2 border-emerald-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-800">
                          Ringkasan Pembelian
                        </h3>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-600">{selectedProduct.name}</span>
                        <span className="font-medium">{formatCurrency(selectedProduct.selling_price)}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-600">Nomor Tujuan</span>
                        <span className="font-medium font-mono">{formatPhoneNumber(phoneNumber)}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-600">Biaya Admin</span>
                        <span className="font-medium text-sky-600">{formatCurrency(adminFee)}</span>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                        <span className="font-semibold text-slate-800">Total Bayar</span>
                        <span className="text-xl font-heading font-bold text-emerald-600">
                          {formatCurrency(totalBayar)}
                        </span>
                      </div>
                    </div>

                    {isAgentLoggedIn() ? (
                      <AgentPaymentButton
                        serviceType="pulsa"
                        customerNo={formatPhoneNumber(phoneNumber)}
                        customerName={`${operator} - ${formatPhoneNumber(phoneNumber)}`}
                        productName={selectedProduct.name}
                        amount={selectedProduct.selling_price}
                        adminFee={adminFee}
                        buyerSkuCode={selectedProduct.sku}
                        onSuccess={() => { 
                          toast.success("Pembelian pulsa berhasil!");
                        }}
                        onReceiptClose={() => {
                          navigate("/");
                        }}
                      />
                    ) : (
                      <Button
                        onClick={handlePurchase}
                        disabled={isProcessing}
                        className="w-full h-14 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all bg-gradient-to-r from-emerald-500 to-teal-600"
                        data-testid="buy-pulsa-btn"
                      >
                        {isProcessing ? (
                          <div className="spinner w-6 h-6" />
                        ) : (
                          "Beli Pulsa"
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
